/**
 * HR Quarterly Holiday Report — 17/06/2026 (Lot 4)
 *
 * Le user a demandé que la gestion des congés bascule en mode autonome :
 *   • Le joueur n'initie plus les congés manuellement (bouton supprimé).
 *   • Chaque chauffeur dont le solde dépasse 5 jours demande des congés —
 *     une notif d'attente apparaît.
 *   • La fonction RH (Nadia Cheikh) gère automatiquement le planning quand
 *     elle est en poste : elle approuve les départs au fil de l'eau via le
 *     mécanisme `triggerAutoHoliday` existant.
 *   • Tous les 90 jours, Nadia (ou le service RH par défaut) envoie un
 *     **rapport trimestriel des congés** dans la messagerie : solde par
 *     chauffeur, jours pris, pression cumulée.
 *
 * Le helper renvoie un payload de message prêt à être push via
 * `pushCustomMessage`. Caller (coreSlice.nextDay) déclenche tous les
 * 90 jours et stamp `lastHolidayQuarterlyReportDay` pour idempotence.
 */

const REPORT_INTERVAL_DAYS = 90;

/**
 * Décide si on doit émettre un rapport aujourd'hui.
 * @returns {boolean}
 */
export function shouldEmitHolidayQuarterlyReport(state) {
  const totalDays = state.totalDays || 0;
  // Premier rapport au jour 90, puis tous les 90 jours.
  if (totalDays < REPORT_INTERVAL_DAYS) return false;
  const last = state.lastHolidayQuarterlyReportDay || 0;
  return (totalDays - last) >= REPORT_INTERVAL_DAYS;
}

/**
 * Construit le message Messagerie pour le rapport trimestriel.
 * @param {Array} drivers
 * @param {number} totalDays
 * @param {boolean} hasNadia — Nadia Cheikh est-elle en poste ?
 * @returns {object} payload pushCustomMessage
 */
export function buildHolidayQuarterlyReport(drivers, totalDays, hasNadia) {
  const sorted = [...(drivers || [])].sort((a, b) => {
    const ra = (a.holidayDaysRemaining || 0);
    const rb = (b.holidayDaysRemaining || 0);
    return rb - ra;
  });

  const quarter = Math.floor((totalDays - 1) / 90) + 1;
  const onHolidayNow = sorted.filter(d => d.onHoliday);
  const pressured = sorted.filter(d => (d.holidayPressureLossThisYear || 0) > 0);
  const overBalance = sorted.filter(d => (d.holidayDaysRemaining || 0) > 20);

  const lines = sorted.map(d => {
    const bal = d.holidayDaysRemaining || 0;
    const pressure = d.holidayPressureLossThisYear || 0;
    const flag = d.onHoliday
      ? ' (en congés)'
      : pressure > 0
        ? ` (pression −${pressure} sat)`
        : '';
    return `• ${d.name} — ${bal} j restants${flag}`;
  }).join('\n');

  const body =
    `Bonjour Patron,\n\n` +
    `Voici le **rapport trimestriel des congés** (Q${quarter}). ` +
    `${hasNadia
      ? `J'ai planifié les départs au fil de l'eau pour éviter toute pression sociale.`
      : `À noter : sans **Responsable RH** (Nadia Cheikh), les congés se déclenchent automatiquement dès que le solde le permet mais aucun arbitrage humain n'est fait. Recruter Nadia améliorerait nettement la planification.`}\n\n` +
    `**Soldes restants** :\n${lines || '• Aucun chauffeur enregistré.'}\n\n` +
    `**Synthèse** :\n` +
    `• ${onHolidayNow.length} chauffeur(s) en congés actuellement\n` +
    `• ${pressured.length} chauffeur(s) sous pression congés (satisfaction impactée)\n` +
    `• ${overBalance.length} chauffeur(s) avec un solde > 20 j (à planifier rapidement)\n\n` +
    `${hasNadia
      ? `Prochain rapport dans 90 jours.\n\n— Nadia Cheikh, Responsable RH`
      : `Prochain rapport dans 90 jours.\n\n— Service RH (sans responsable dédié)`}`;

  return {
    senderType: 'support',
    senderName: hasNadia ? 'Nadia Cheikh (Resp. RH)' : 'Service RH',
    senderRole: hasNadia ? 'role-nadia-cheikh' : null,
    tone: 'neutral',
    kind: 'info',
    subject: `Rapport trimestriel des congés — Q${quarter}`,
    body,
    ttlDays: 30,
  };
}

export const HOLIDAY_REPORT_CONSTANTS = {
  INTERVAL_DAYS: REPORT_INTERVAL_DAYS,
};
